/** @jsx isolateComponent */

import { InterpolationWithTheme } from '@emotion/core';
import { isolateComponent, withModifier } from '@zapier/style-encapsulation';
import { Animation, Colors, Tooltip } from '@zapier/design-system';

export const DOT_SIZE = '30px';

export const ACTIVE = 'ACTIVE';
export const ENABLED = 'ENABLED';
export const DISABLED = 'DISABLED';

export type DotState = typeof ACTIVE | typeof ENABLED | typeof DISABLED;
export const states: DotState[] = [ACTIVE, ENABLED, DISABLED];

type NavDotProps = {
  /** Number that is displayed in the dot. */
  number: number;
  /** Callback function that is fired on click. */
  onClick: () => void;
  /** Called when the dot receives `focus` or `mouseenter`. */
  onFocus: () => void;
  /** Called when the button receives `blur` or `mouseout`. */
  onBlur: () => void;
  /** Which state this dot is in. */
  state: DotState;
  /** A tooltip that conditionally displays. */
  tooltip: string;
  /** True if this dots tooltip should be visible */
  showTooltip: boolean;
};

const Styles = {
  root: (state: DotState) => [
    {
      height: DOT_SIZE,
      width: DOT_SIZE,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 'bold',
      fontSize: '16px',
      position: 'relative',

      // This is the outline that renders around the button when
      // it's focused. It's a pseudo element to preserve transparency
      // between the edge of the button and the edge of the outline.
      // It's defined here vs only in `:focus` so that it can transition
      // between states.
      '&::before': {
        content: '""',
        position: 'absolute',
        pointerEvents: 'none',
        border: '2px solid transparent',
        borderRadius: '36px',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        transitionProperty: 'all',
        transitionTimingFunction: Animation.transitionTimingFunction,
        transitionDuration: Animation.transitionDuration,
      },
    },
    withModifier(
      state,
      {
        [ENABLED]: {
          cursor: 'pointer',
          background: Colors.GrayWarm2,
          '&:hover': {
            background: Colors.UiPrimaryWeakest,
          },
          '&:focus': {
            '&::before': {
              borderColor: Colors.UiPrimaryStrongest,
              top: -3,
              right: -3,
              bottom: -3,
              left: -3,
            },
          },
        },
        [ACTIVE]: {
          background: Colors.PrimeBlack,
          color: Colors.PrimeWhite,
          '&:hover': {
            background: Colors.UiPrimaryStrongest,
          },
        },
        [DISABLED]: {
          background: Colors.GrayWarm2,
          color: Colors.GrayWarm6,
          '&:focus': {
            '&::before': {
              borderColor: Colors.UiPrimaryStrongest,
              top: -3,
              right: -3,
              bottom: -3,
              left: -3,
            },
          },
        },
      }[state]
    ),
  ],
};

/**
 * Renders a clickable nav dot.
 */
const NavDot = ({
  number,
  onClick,
  state,
  tooltip,
  showTooltip = false,
  onFocus,
  onBlur,
}: NavDotProps) => {
  const enabledOnlyProps =
    state === ENABLED
      ? {
          onClick,
          onFocus,
          onMouseEnter: onFocus,
        }
      : {};

  return (
    <Tooltip content={tooltip} isOpen={showTooltip} placement="top">
      <button
        css={Styles.root(state) as InterpolationWithTheme<any>}
        disabled={state !== ENABLED}
        onBlur={onBlur}
        onMouseOut={onBlur}
        type="button"
        {...enabledOnlyProps}
      >
        {number}
      </button>
    </Tooltip>
  );
};

export default NavDot;
